/** @jsxRuntime classic */
/** @jsx jsx */

import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Colors } from '@zapier/design-system';
import { MEDIA_MIN_WIDTH } from '../../constants';
import {
  useAvroTracking,
  EventAction,
  InteractionName,
} from '../../observability';

interface TextTrack {
  default: boolean;
  label: string;
  language: string;
  textTrackUrl: string;
  type: 'subtitles' | 'captions';
}

export interface VideoProps {
  autoPlay?: boolean;
  fullHeight?: boolean;
  hideControls?: boolean;
  loop?: boolean;
  onLoadedData?: () => void;
  onPlay?: () => void;
  playbackRate?: number;
  posterImageUrl?: string;
  textTracks?: TextTrack[];
  title: string;
  videoUrl: string;
}

const Styles = labelStyles('Video', {
  root: css({
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 'fit-content',
    maxWidth: '100%',
    maxHeight: '100%',
    margin: '0 auto',
    height: '100%',
  }),

  overlay: css({
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    cursor: 'pointer',
    border: 'none',
  }),

  playIcon: css({
    width: '64px',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 253, 249, 0.8)',
    color: Colors.neutral700,
    borderRadius: '50%',
    transition: 'all 250ms',

    [MEDIA_MIN_WIDTH.small]: {
      width: '96px',
      height: '96px',
    },

    [MEDIA_MIN_WIDTH.small]: {
      width: '128px',
      height: '128px',
    },

    'button:hover &': {
      backgroundColor: 'rgba(255, 253, 249, 1)',
      transform: 'scale(0.95)',
      color: Colors.neutral800,
    },

    '& > svg': css({
      width: '50%',
      marginLeft: '7.5%',
    }),
  }),

  video: (fullHeight: boolean) =>
    css({
      display: 'block',
      maxWidth: '100%',
      maxHeight: '100%',
      ...(fullHeight && { height: '100%' }),
    }),
});

export function Video(props: VideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasBeenStarted, setHasBeenStarted] = useState(false);
  const [textTracks, setTextTracks] = useState<TextTrack[]>([]);

  const { emitUserInteractionEvent } = useAvroTracking();

  useEffect(() => {
    if (props.textTracks) {
      Promise.all(
        props.textTracks.map(async (track) => {
          const response = await fetch(track.textTrackUrl);

          if (!response.ok) {
            return track;
          }

          const blob = await response.blob();

          return {
            ...track,
            textTrackUrl: URL.createObjectURL(blob),
          };
        }),
      ).then((tracks) => {
        setTextTracks(tracks);
      });
    }
  }, [props.textTracks]);

  useEffect(() => {
    if (
      videoRef.current &&
      props.playbackRate &&
      props.playbackRate >= 0 &&
      props.playbackRate !== videoRef.current.playbackRate
    ) {
      videoRef.current.playbackRate = props.playbackRate;
    }
  }, [props.playbackRate]);

  const play = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const handlePlayEvent = (event: SyntheticEvent<HTMLVideoElement, Event>) => {
    if (props.onPlay) props.onPlay();
    setHasBeenStarted(true);

    // Only emit a play event when triggered by a user, not by auto play
    if (
      !props.hideControls &&
      (!props.autoPlay || event.currentTarget.currentTime > 0)
    ) {
      emitUserInteractionEvent({
        event_action: EventAction.click,
        interaction_name: InteractionName.video,
        option_selected: 'play',
        custom_tracking_fields: {
          current_time: event.currentTarget.currentTime.toString(),
        },
      });
    }
  };

  const handlePauseEvent = (event: SyntheticEvent<HTMLVideoElement, Event>) => {
    // Only emit a pause event when triggered by a user, not by the video ending
    if (event.currentTarget.currentTime < event.currentTarget.duration) {
      emitUserInteractionEvent({
        event_action: EventAction.click,
        interaction_name: InteractionName.video,
        option_selected: 'pause',
        custom_tracking_fields: {
          current_time: event.currentTarget.currentTime.toString(),
        },
      });
    }
  };

  const handleSeekedEvent = (
    event: SyntheticEvent<HTMLVideoElement, Event>,
  ) => {
    // Only emit a seek event when triggered by a user, not by the video looping
    if (!props.loop || (props.loop && event.currentTarget.currentTime > 0.01)) {
      emitUserInteractionEvent({
        event_action: EventAction.click,
        interaction_name: InteractionName.video,
        option_selected: 'seek',
        custom_tracking_fields: {
          current_time: event.currentTarget.currentTime.toString(),
        },
      });
    }
  };

  return (
    <div css={Styles.root}>
      {!props.autoPlay && !hasBeenStarted && (
        <button css={Styles.overlay} data-testid="video-overlay" onClick={play}>
          <div css={Styles.playIcon}>
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M19.5 12L5 3.63V20.37L19.5 12Z" />
            </svg>
          </div>
        </button>
      )}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        autoPlay={props.autoPlay}
        controls={hasBeenStarted && !props.hideControls}
        controlsList="nodownload noplaybackrate"
        css={Styles.video(props.fullHeight)}
        data-testid="video"
        disablePictureInPicture={true}
        disableRemotePlayback={true}
        loop={props.loop}
        muted={props.autoPlay}
        onLoadedData={props.onLoadedData}
        onPlay={handlePlayEvent}
        playsInline={props.autoPlay}
        poster={props.posterImageUrl}
        preload={props.autoPlay ? 'auto' : 'metadata'}
        ref={videoRef}
        src={props.videoUrl}
        title={props.title}
        {...(!props.hideControls && {
          onSeeked: handleSeekedEvent,
          onPause: handlePauseEvent,
        })}
      >
        {textTracks.map((track, index) => (
          <track
            default={track.default}
            key={index}
            kind={track.type}
            label={track.label}
            src={track.textTrackUrl}
            srcLang={track.language}
          />
        ))}
      </video>
    </div>
  );
}
